'use client';

import { useEffect, useMemo, useState, type ChangeEvent } from 'react';
import { useTranslations } from 'next-intl';
import { HStack, VStack } from '@terra/ui-styled-system';
import { Cancel01Icon } from '@terra/ui-icons';
import { Button } from '../../components/button';
import { LegacyDialog } from '../../components/dialog';
import { IconButton } from '../../components/icon-button';
import { Input } from '../../components/input';
import { Select, createSelectListCollection } from '../../components/select';
import { Text } from '../../components/text';
import {
  isLinkedTicketProvider,
  type LinkedTicketProvider,
} from '@terra/shared-contracts-findings';
import { translateLinkedTicketOverview } from './linked-ticket-taxonomy';
import { toasterApi } from '../toast/ToastProvider';

type EditLinkedTicketDialogProps = {
  isOpen: boolean;
  mode?: 'link' | 'edit';
  provider: LinkedTicketProvider | null;
  connectedProviders?: LinkedTicketProvider[];
  ticketUrl: string;
  isReplacingLinkedTicket: boolean;
  onClose: () => void;
  onReplaceLinkedTicket: (params: {
    provider: LinkedTicketProvider;
    ticketUrl: string;
  }) => Promise<void>;
};

export function EditLinkedTicketDialog({
  isOpen,
  mode = 'edit',
  provider,
  connectedProviders = [],
  ticketUrl,
  isReplacingLinkedTicket,
  onClose,
  onReplaceLinkedTicket,
}: EditLinkedTicketDialogProps) {
  const t = useTranslations(
    'designSystem.findings.findingDrawer.tabs.overview',
  );
  const [editTicketUrl, setEditTicketUrl] = useState(ticketUrl);
  const [selectedProvider, setSelectedProvider] =
    useState<LinkedTicketProvider | null>(null);
  const [editTicketError, setEditTicketError] = useState<string | null>(null);
  const resolvedProvider =
    mode === 'edit' ? provider : (selectedProvider ?? null);
  const linkedTicketCopy = (key: string) =>
    translateLinkedTicketOverview({
      t,
      provider: resolvedProvider,
      key,
    });
  const showProviderSelect = mode === 'link' && connectedProviders.length > 1;
  const providerOptions = useMemo(
    () =>
      connectedProviders.map((connectedProvider) => ({
        value: connectedProvider,
        label: t(`linkedTicketProviders.${connectedProvider}`),
      })),
    [connectedProviders, t],
  );
  const providerCollection = useMemo(
    () =>
      createSelectListCollection({
        items: providerOptions,
        itemToValue: (item) => item.value,
        itemToString: (item) => item.label,
      }),
    [providerOptions],
  );

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setEditTicketUrl(ticketUrl);
    setEditTicketError(null);
    setSelectedProvider(provider ?? connectedProviders[0] ?? null);
  }, [connectedProviders, isOpen, provider, ticketUrl]);
  const handleClose = () => {
    if (isReplacingLinkedTicket) {
      return;
    }

    onClose();
  };

  const handleSaveLinkedTicket = async () => {
    if (!resolvedProvider) {
      return;
    }

    const nextTicketUrl = editTicketUrl.trim();

    if (!nextTicketUrl) {
      return;
    }

    try {
      setEditTicketError(null);
      await onReplaceLinkedTicket({
        provider: resolvedProvider,
        ticketUrl: nextTicketUrl,
      });
      toasterApi.success({
        title: linkedTicketCopy('ticketUpdatedSuccessfully'),
      });
      onClose();
    } catch (error) {
      setEditTicketError(
        error instanceof Error
          ? error.message
          : linkedTicketCopy('editLinkedTicketError'),
      );
    }
  };

  const dialogTitle =
    mode === 'link'
      ? linkedTicketCopy('linkLinkedTicketTitle')
      : linkedTicketCopy('editLinkedTicketTitle');

  return (
    <LegacyDialog.Root
      open={isOpen}
      onOpenChange={({ open }) => {
        if (!open) {
          handleClose();
        }
      }}
    >
      <LegacyDialog.Backdrop />
      <LegacyDialog.Positioner>
        <LegacyDialog.Content maxW="520px" w="calc(100vw - 32px)" p={0}>
          <VStack gap={0} alignItems="stretch" w="full">
            <HStack
              justifyContent="space-between"
              alignItems="center"
              px={6}
              pt={6}
              pb={3}
            >
              <LegacyDialog.Title>{dialogTitle}</LegacyDialog.Title>
              <LegacyDialog.CloseTrigger asChild>
                <IconButton
                  variant="ghost"
                  size="sm"
                  aria-label={t('closeEditLinkedTicket')}
                  disabled={isReplacingLinkedTicket}
                >
                  <Cancel01Icon size={16} />
                </IconButton>
              </LegacyDialog.CloseTrigger>
            </HStack>
            <VStack gap={3} alignItems="stretch" px={6} py={3}>
              {showProviderSelect ? (
                <VStack gap={1.5} alignItems="stretch">
                  <Text fontSize="sm">{t('linkedTicketProviderLabel')}</Text>
                  <Select.Root
                    collection={providerCollection}
                    value={resolvedProvider ? [resolvedProvider] : []}
                    onValueChange={({ value }) => {
                      const nextProvider = value[0];

                      if (
                        nextProvider &&
                        isLinkedTicketProvider(nextProvider)
                      ) {
                        setSelectedProvider(nextProvider);
                      }
                    }}
                    size="sm"
                  >
                    <Select.Control>
                      <Select.Trigger>
                        <Select.ValueText />
                      </Select.Trigger>
                    </Select.Control>
                    <Select.Positioner>
                      <Select.Content>
                        {providerOptions.map((option) => (
                          <Select.Item key={option.value} item={option}>
                            <Select.ItemText>{option.label}</Select.ItemText>
                          </Select.Item>
                        ))}
                      </Select.Content>
                    </Select.Positioner>
                  </Select.Root>
                </VStack>
              ) : null}
              <VStack gap={1.5} alignItems="stretch">
                <Text fontSize="sm">{linkedTicketCopy('ticketUrlLabel')}</Text>
                <Input
                  value={editTicketUrl}
                  onChange={(event: ChangeEvent<HTMLInputElement>) => {
                    setEditTicketUrl(event.target.value);
                    if (editTicketError) {
                      setEditTicketError(null);
                    }
                  }}
                  placeholder={linkedTicketCopy('ticketUrlPlaceholder')}
                  disabled={isReplacingLinkedTicket}
                />
                <Text fontSize="xs" color="fg.muted">
                  {linkedTicketCopy('ticketLabelDisclaimer')}
                </Text>
                {editTicketError ? (
                  <Text fontSize="xs" color="fg.error">
                    {editTicketError}
                  </Text>
                ) : null}
              </VStack>
            </VStack>
            <HStack justifyContent="flex-end" gap={3} px={6} pt={3} pb={6}>
              <Button
                variant="outline"
                onClick={handleClose}
                disabled={isReplacingLinkedTicket}
              >
                {t('cancelEditLinkedTicket')}
              </Button>
              <Button
                onClick={handleSaveLinkedTicket}
                loading={isReplacingLinkedTicket}
                disabled={!editTicketUrl.trim() || !resolvedProvider}
              >
                {t('saveEditLinkedTicket')}
              </Button>
            </HStack>
          </VStack>
        </LegacyDialog.Content>
      </LegacyDialog.Positioner>
    </LegacyDialog.Root>
  );
}
